import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { LanguageProvider, useLanguage } from "@/contexts/LanguageContext";
import { useEffect } from "react";
import { Toaster } from "sonner";
import ClientRequestPage from "@/pages/ClientRequestPage";
import SuccessPage from "@/pages/SuccessPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import DashboardPage from "@/pages/DashboardPage";
import QRCodePage from "@/pages/QRCodePage";
import SettingsPage from "@/pages/SettingsPage";
import EventsPage from "@/pages/EventsPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
// Landing page (public)
import LandingPage from "@/pages/LandingPage";
// Legal pages (public, no auth required)
import PrivacyPage from "@/pages/legal/PrivacyPage";
import ConditionsPage from "@/pages/legal/ConditionsPage";
import MentionsLegalesPage from "@/pages/legal/MentionsLegalesPage";

// Redirect from preview to production (configurable via env)
const PROD_HOST = process.env.REACT_APP_PROD_HOST || "";

const RedirectToProduction = () => {
  useEffect(() => {
    // Only redirect if PROD_HOST is configured and we're on preview
    if (PROD_HOST) {
      const currentHost = window.location.hostname;
      if (currentHost.includes("preview.emergentagent.com")) {
        const newUrl = `https://${PROD_HOST}${window.location.pathname}${window.location.search}`;
        window.location.replace(newUrl);
      }
    }
  }, []);
  return null;
};

// Sync user's preferred language when authenticated
const LanguageSync = () => {
  const { user, isAuthenticated } = useAuth();
  const { syncWithUserLanguage } = useLanguage();
  
  useEffect(() => {
    if (isAuthenticated && user?.preferred_language) {
      syncWithUserLanguage(user.preferred_language);
    }
  }, [isAuthenticated, user?.preferred_language, syncWithUserLanguage]);
  
  return null;
};

const ProtectedRoute = ({ children }) => {
  const { loading, isAuthenticated } = useAuth();
  const { t } = useLanguage();
  
  // Vérifier token dans localStorage directement
  const hasToken = !!localStorage.getItem('access_token');
  
  console.log('[ROUTE] ProtectedRoute check - loading:', loading, 'isAuthenticated:', isAuthenticated, 'hasToken:', hasToken);
  
  // Attendre que l'init soit terminé
  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-neon-cyan animate-pulse font-heading text-2xl">{t('common', 'loading')}</div>
      </div>
    );
  }
  
  // Autoriser si token présent OU isAuthenticated
  if (!hasToken && !isAuthenticated) {
    console.log('[ROUTE] No auth - redirect to /login');
    return <Navigate to="/login" replace />;
  }
  
  console.log('[ROUTE] Auth OK - rendering protected content');
  return children;
};

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <LanguageSync />
        <RedirectToProduction />
        <BrowserRouter>
          <Routes>
            {/* Public routes */}
            <Route path="/request/:djId" element={<ClientRequestPage />} />
            <Route path="/success" element={<SuccessPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            
            {/* Legal pages (public, no auth required) */}
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/conditions" element={<ConditionsPage />} />
            <Route path="/mentions-legales" element={<MentionsLegalesPage />} />
            
            {/* Protected routes */}
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            } />
            <Route path="/qrcode" element={
              <ProtectedRoute>
                <QRCodePage />
              </ProtectedRoute>
            } />
            <Route path="/settings" element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            } />
            <Route path="/events" element={
              <ProtectedRoute>
                <EventsPage />
              </ProtectedRoute>
            } />
            
            {/* Landing page - handles redirect to dashboard if authenticated */}
            <Route path="/" element={<LandingPage />} />
          </Routes>
        </BrowserRouter>
        {/* Toast notifications - global */}
        <Toaster 
          position="bottom-center"
          toastOptions={{
            style: {
              background: '#1a1a1a',
              border: '1px solid rgba(255,255,255,0.1)',
              color: '#fff',
            },
            className: 'toast-custom',
          }}
          richColors
        />
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
